/**
 * Tauri Desktop OAuth Helper
 * Handles Google OAuth via deep links for the Tauri desktop app.
 * 
 * Flow:
 * 1. Generate Supabase OAuth URL with PKCE (skipBrowserRedirect: true)
 * 2. Open URL in default browser via tauri-plugin-shell
 * 3. User completes Google auth in browser
 * 4. Supabase redirects to visualgrid://auth/callback?code=XXX
 * 5. Deep link handler catches the URL, extracts code
 * 6. Exchange code for session via supabase.auth.exchangeCodeForSession()
 */

import { supabase } from './supabaseClient';

const DEEP_LINK_SCHEME = 'visualgrid';
const DEEP_LINK_CALLBACK = `${DEEP_LINK_SCHEME}://auth/callback`;

/**
 * Detect if running inside a Tauri desktop app
 */
export function isTauriApp(): boolean {
  if (typeof window === 'undefined') return false;
  return !!(window as any).__TAURI_INTERNALS__;
}

/**
 * Start Google OAuth flow for Tauri desktop.
 * Generates a PKCE OAuth URL and opens it in the system browser.
 */
export async function startTauriGoogleOAuth(): Promise<void> {
  // Use production URL for callback since window.location.origin in Tauri
  // returns tauri://localhost instead of the actual web domain
  const webCallbackUrl = 'https://visualgrid.app/auth/callback?source=tauri';
  
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: webCallbackUrl,
      skipBrowserRedirect: true,
      queryParams: {
        access_type: 'offline',
        prompt: 'consent',
      },
    },
  });

  if (error) throw error;
  if (!data.url) throw new Error('No OAuth URL returned');

  // Open the OAuth URL in the user's default browser
  const { open } = await import('@tauri-apps/plugin-shell');
  await open(data.url);
}

/**
 * Parse an auth code from a deep link URL.
 * Expected format: visualgrid://auth/callback?code=XXX
 */
export function parseAuthCodeFromUrl(url: string): string | null {
  try {
    // Deep link URLs may come as visualgrid://auth/callback?code=XXX
    // URL constructor needs a valid scheme, replace custom scheme with https
    const normalizedUrl = url.replace(`${DEEP_LINK_SCHEME}://`, 'https://visualgrid.app/');
    const parsed = new URL(normalizedUrl);
    return parsed.searchParams.get('code');
  } catch {
    return null;
  }
}

/**
 * Exchange an auth code for a Supabase session (PKCE flow).
 * The code_verifier is automatically read from localStorage by Supabase.
 */
export async function exchangeCodeForSession(code: string) {
  const { data, error } = await supabase.auth.exchangeCodeForSession(code);
  if (error) throw error;
  return data;
}

/**
 * Initialize the deep link listener for OAuth callbacks.
 * Listens to the Tauri `deep-link-received` event emitted from Rust.
 * Returns an unlisten function to clean up.
 */
export async function initDeepLinkAuthListener(
  onSuccess: (session: any) => void,
  onError: (error: Error) => void,
): Promise<() => void> {
  if (!isTauriApp()) {
    return () => {};
  }

  const { listen } = await import('@tauri-apps/api/event');

  const unlisten = await listen<string[]>('deep-link-received', async (event) => {
    const urls = event.payload;
    if (!urls || urls.length === 0) return;

    for (const url of urls) {
      // Only process auth callback URLs
      if (!url.startsWith(`${DEEP_LINK_SCHEME}://auth/callback`)) continue;

      const code = parseAuthCodeFromUrl(url);
      if (!code) {
        onError(new Error('No auth code found in deep link URL'));
        return;
      }

      try {
        const data = await exchangeCodeForSession(code);
        if (data.session) {
          onSuccess(data.session);
        } else {
          onError(new Error('No session returned after code exchange'));
        }
      } catch (err) {
        onError(err instanceof Error ? err : new Error(String(err)));
      }
    }
  });

  return unlisten;
}
